// src/components/users/UserTable.jsx

import React, { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import EditUserModal from './EditUserModal';
import ConfirmationModal from '../ConfirmationModal';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';

export default function UserTable({ users, onUserUpdate, onUserDeleted }) {
  const intl = useIntl();
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const { user: currentUser } = useContext(AuthContext);

  const handleEdit = (user) => {
    setEditingUser(user);
  };

  const handleCloseModal = () => {
    setEditingUser(null);
    setUserToDelete(null);
  };

  const handleSave = (updatedUser) => {
    onUserUpdate(updatedUser);
    handleCloseModal();
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteDoc(doc(db, 'users', userToDelete.id));
      toast.success(intl.formatMessage({ id: 'user.deletedSuccess' }));
      onUserDeleted(userToDelete.id);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'user.deletedError' }));
      console.error("Error deleting user:", error);
    } finally {
      handleCloseModal();
    }
  };

  const handleToggleStatus = async (user) => {
    const userRef = doc(db, 'users', user.id);
    await updateDoc(userRef, { active: !user.active });
    // A atualização da UI será feita pelo listener onSnapshot no componente pai (Users.jsx)
  };

  return (
    <>
      <table className="user-table">
        <thead>
          <tr>
            <th>{intl.formatMessage({ id: 'user.name' })}</th>
            <th>{intl.formatMessage({ id: 'user.email' })}</th>
            <th>{intl.formatMessage({ id: 'user.role' })}</th>
            <th>{intl.formatMessage({ id: 'user.status' })}</th>
            <th>{intl.formatMessage({ id: 'user.actions' })}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>
                <span className={`status-badge ${user.active ? 'active' : 'inactive'}`}>
                  {user.active ? intl.formatMessage({ id: 'user.active' }) : intl.formatMessage({ id: 'user.inactive' })}
                </span>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => handleEdit(user)}
                    className="secondary-button"
                    title={intl.formatMessage({ id: 'user.edit.tooltip', defaultMessage: 'Editar dados do usuário' })}
                  >
                    {intl.formatMessage({ id: 'user.edit' })}
                  </button>
                  <button
                    onClick={() => handleToggleStatus(user)}
                    className="secondary-button"
                    title={user.active
                        ? intl.formatMessage({ id: 'user.deactivate.tooltip', defaultMessage: 'Desativar este usuário' })
                        : intl.formatMessage({ id: 'user.activate.tooltip', defaultMessage: 'Ativar este usuário' })
                    }
                  >
                    {user.active ? intl.formatMessage({ id: 'user.deactivate' }) : intl.formatMessage({ id: 'user.activate' })}
                  </button>
                  <button onClick={() => handleDeleteClick(user)} className="danger-button" title={intl.formatMessage({ id: 'user.delete.tooltip', defaultMessage: 'Remover este usuário permanentemente' })}>
                    {intl.formatMessage({ id: 'user.delete' })}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={handleCloseModal}
          onUserUpdated={handleSave}
        />
      )}
      {userToDelete && (
        <ConfirmationModal
          isOpen={!!userToDelete}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          title={intl.formatMessage({ id: 'user.confirmDeleteTitle' })}
          message={intl.formatMessage({ id: 'user.confirmDeleteMessage' }, { name: userToDelete.name })}
        />
      )}
    </>
  );
}
